import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import type {
	ESignField,
	ESignObject,
	ESignRecipient,
	ESignRecipientStatus,
	SetESignObjectContentDataProps,
} from '../../lib/api/types/esign.interfaces'
import type {
	EnvelopeField,
	FieldInput,
	FlowMode,
} from '../../lib/api/types/esign.interfaces'
import {
	INITIALS_META,
	type RecipientLite,
	resolveESignFieldType,
} from '../../lib/utils/esign'

export interface ESignRecipientForm {
	id?: string
	name: string
	email: string
	role: 'SIGNER' | 'VIEWER'
	signingOrder?: number | null
	status?: ESignRecipientStatus | null
}

export interface ESignObjectFormValues {
	title: string
	message: string
	flowMode: FlowMode
	recipients: ESignRecipientForm[]
	fields: ESignField[]
}

const DEFAULT_VALUES: ESignObjectFormValues = {
	title: '',
	message: '',
	flowMode: 'SEQUENTIAL',
	recipients: [{ name: '', email: '', role: 'SIGNER' }],
	fields: [],
}

export function buildESignRecipientId(
	index: number,
	existingId?: string | null,
) {
	if (typeof existingId === 'string' && existingId.trim()) {
		return existingId.trim()
	}

	return `recipient-${index + 1}`
}

function normalizeCoordinate(value: number | null | undefined) {
	if (typeof value !== 'number' || Number.isNaN(value)) return 0
	return value <= 1 ? value * 100 : value
}

function toRecipientForm(recipient: ESignRecipient): ESignRecipientForm {
	return {
		id: recipient.id,
		name: recipient.name ?? '',
		email: recipient.email ?? '',
		role: recipient.role ?? 'SIGNER',
		signingOrder: recipient.signingOrder ?? null,
		status: recipient.status ?? null,
	}
}

export function eSignFieldsToEnvelopeFields(
	fields: ESignField[] | undefined | null,
	recipients: ESignRecipient[] | ESignRecipientForm[] | RecipientLite[] = [],
): EnvelopeField[] {
	if (!Array.isArray(fields)) return []

	const firstSigner =
		recipients.find((recipient) => recipient.role === 'SIGNER') ?? null

	return fields.map((field, index) => {
		const upperType = String(field.type ?? 'TEXT').toUpperCase()
		const envelopeType = resolveESignFieldType({
			type: field.type,
			label: field.label,
			meta: field.meta as Record<string, unknown> | null | undefined,
		})
		const isNativeType =
			envelopeType === 'SIGNATURE' ||
			envelopeType === 'INITIALS' ||
			envelopeType === 'TEXT'
		const normalizedMeta =
			field.meta && typeof field.meta === 'object' ? { ...field.meta } : {}

		return {
			id: field.id,
			recipientId:
				typeof field.recipientId === 'string' && field.recipientId
					? field.recipientId
					: buildESignRecipientId(
							index,
							String(
								(firstSigner as any)?.id ??
									(firstSigner as any)?.recipientId ??
									'',
							),
						),
			page: Number(field.page ?? 1),
			positionX: normalizeCoordinate(field.x),
			positionY: normalizeCoordinate(field.y),
			width: normalizeCoordinate(field.width),
			height: normalizeCoordinate(field.height),
			type: isNativeType ? envelopeType : 'TEXT',
			fieldMeta: {
				...normalizedMeta,
				type:
					envelopeType === 'SIGNATURE'
						? 'signature'
						: envelopeType === 'INITIALS'
							? 'initials'
							: 'text',
				label: field.label ?? null,
				placeholder: field.placeholder ?? null,
				required: field.required ?? false,
				preset: isNativeType ? undefined : upperType,
			},
		}
	})
}

export function fieldInputsToESignFields(fields: FieldInput[]): ESignField[] {
	return fields.map((field) => {
		const fieldMeta = { ...(field.fieldMeta ?? {}) } as Record<string, unknown>
		const preset = String(fieldMeta.preset ?? '').toUpperCase()
		const preservedFieldId =
			typeof fieldMeta.__fieldId === 'string'
				? String(fieldMeta.__fieldId)
				: undefined
		const inputType = String(field.type ?? '').toUpperCase()

		const resolvedType =
			inputType === 'SIGNATURE'
				? 'SIGNATURE'
				: inputType === 'INITIALS'
					? 'INITIALS'
					: preset || 'TEXT'

		if (resolvedType === 'INITIALS') {
			delete fieldMeta.type
			delete fieldMeta.preset
		}

		const nextField: ESignField = {
			id: preservedFieldId,
			recipientId: String(field.recipientId),
			type: resolvedType as ESignField['type'],
			page: Number(field.pageNumber ?? 1),
			x: Number(field.pageX ?? 0),
			y: Number(field.pageY ?? 0),
			width: Number(field.pageWidth ?? 0),
			height: Number(field.pageHeight ?? 0),
			required:
				typeof fieldMeta.required === 'boolean'
					? fieldMeta.required
					: resolvedType === 'INITIALS'
						? INITIALS_META.required
						: false,
			value: null,
			label:
				typeof fieldMeta.label === 'string'
					? String(fieldMeta.label)
					: resolvedType === 'INITIALS'
						? INITIALS_META.label
						: null,
			placeholder:
				typeof fieldMeta.placeholder === 'string'
					? String(fieldMeta.placeholder)
					: resolvedType === 'INITIALS'
						? INITIALS_META.placeholder
						: null,
			meta: Object.keys(fieldMeta).length ? fieldMeta : null,
		}

		return nextField
	})
}

export function normalizeESignFieldsForApi(
	fields: ESignField[] | undefined | null,
): ESignField[] {
	if (!Array.isArray(fields)) return []

	return fields.map((field) => {
		const resolvedType = resolveESignFieldType(field)

		if (resolvedType === 'INITIALS') {
			const meta =
				field.meta && typeof field.meta === 'object'
					? { ...(field.meta as Record<string, unknown>) }
					: {}
			delete meta.type
			delete meta.preset

			return {
				...field,
				type: 'INITIALS',
				label: field.label ?? INITIALS_META.label,
				placeholder: field.placeholder ?? INITIALS_META.placeholder,
				required: field.required ?? INITIALS_META.required,
				meta: Object.keys(meta).length ? meta : null,
			}
		}

		return { ...field, type: resolvedType as ESignField['type'] }
	})
}

function toFormValues(eSignObject: ESignObject): ESignObjectFormValues {
	return {
		title: eSignObject.title ?? '',
		message: eSignObject.message ?? '',
		flowMode: eSignObject.flowMode ?? 'SEQUENTIAL',
		recipients:
			eSignObject.recipients?.length > 0
				? eSignObject.recipients.map(toRecipientForm)
				: DEFAULT_VALUES.recipients,
		fields: Array.isArray(eSignObject.fields) ? eSignObject.fields : [],
	}
}

export function useESignObjectForm() {
	const form = useForm<ESignObjectFormValues>({
		defaultValues: DEFAULT_VALUES,
		mode: 'onChange',
	})

	const recipients = form.watch('recipients')
	const fields = form.watch('fields')
	const title = form.watch('title')
	const message = form.watch('message')
	const flowMode = form.watch('flowMode')

	const setRecipients = useCallback(
		(nextRecipients: ESignRecipientForm[]) => {
			form.setValue('recipients', nextRecipients, {
				shouldDirty: true,
				shouldTouch: true,
			})
		},
		[form],
	)

	const setFields = useCallback(
		(nextFields: ESignField[]) => {
			form.setValue('fields', nextFields, {
				shouldDirty: true,
				shouldTouch: true,
			})
		},
		[form],
	)

	const fillFromESignObject = useCallback(
		(eSignObject: ESignObject | null | undefined) => {
			if (!eSignObject) return
			form.reset(toFormValues(eSignObject))
		},
		[form],
	)

	const toSavePayload = useCallback(
		(): SetESignObjectContentDataProps =>
			buildESignObjectSavePayload(form.getValues()),
		[form],
	)

	return {
		form,
		recipients,
		fields,
		title,
		message,
		flowMode,
		setRecipients,
		setFields,
		fillFromESignObject,
		toSavePayload,
	}
}

export function buildESignObjectSavePayload(
	values: ESignObjectFormValues,
): SetESignObjectContentDataProps {
	let signerOrder = 0
	const isSignerField = (recipientId: string) =>
		values.recipients.some(
			(recipient, index) =>
				recipient.role === 'SIGNER' &&
				buildESignRecipientId(index, recipient.id) === recipientId,
		)

	return {
		title: values.title,
		message: values.message,
		flowMode: values.flowMode,
		recipients: values.recipients.map((recipient, index) => ({
			id: buildESignRecipientId(index, recipient.id),
			name: recipient.name,
			email: recipient.email,
			role: recipient.role,
			signingOrder: recipient.role === 'SIGNER' ? (signerOrder += 1) : null,
			status: (recipient.role === 'SIGNER'
				? 'PENDING'
				: null) as ESignRecipientStatus | null,
		})),
		fields: normalizeESignFieldsForApi(
			values.fields.filter((field) =>
				isSignerField(String(field.recipientId ?? '')),
			),
		),
	}
}
